"use client";

import type { ElementType, ReactNode } from "react";

import { useCcLatchInView } from "@/lib/use-cc-latch-in-view";

type CcUsRevealProps = {
  as?: ElementType;
  className?: string;
  id?: string;
  children: ReactNode;
};

/**
 * Sets `data-inview="true"` once the element scrolls into view; the CSS does
 * the rest (masked line slides, image wipes). Content is real text in the
 * page from the start, so nothing depends on JavaScript to be read.
 */
export function CcUsReveal({ as: Tag = "div", className, id, children }: CcUsRevealProps) {
  const { ref, isInView } = useCcLatchInView<HTMLElement>({ threshold: 0.25 });
  return (
    <Tag ref={ref} id={id} className={className} data-inview={isInView ? "true" : "false"}>
      {children}
    </Tag>
  );
}

/** A heading split into masked lines that slide up one after another. */
export function CcUsLines({
  as = "h2",
  id,
  className,
  lines,
}: {
  as?: ElementType;
  id?: string;
  className?: string;
  lines: string[];
}) {
  return (
    <CcUsReveal as={as} id={id} className={`cc-us-lines ${className ?? ""}`}>
      {lines.map((line, index) => (
        <span key={line} className="cc-us-lines__mask">
          <span className="cc-us-lines__line" style={{ transitionDelay: `${index * 90}ms` }}>
            {line}
          </span>{" "}
        </span>
      ))}
    </CcUsReveal>
  );
}
